import { useQuery, useMutation } from "./urqlHooks";

// =============================================
// QUERIES
// =============================================

const GET_MATCH_RECOMMENDATIONS = `
  query GetMatchRecommendations($filter: MatchRecommenderFilterInput!, $pagination: MatchRecommenderPaginationInput) {
    matchRecommendations(filter: $filter, pagination: $pagination) {
      items {
        id
        company_id
        pattern_hash
        type
        status
        target_transaction_id
        target_transaction
        detected_pattern {
          description_keywords
          transaction_type
          amount_min
          amount_max
          amount_exact
          provider_rut
          provider_name
          account_number
          obligation_type
          obligation_family
        }
        stats {
          occurrence_count
          first_seen
          last_seen
          consistency_score
          avg_amount
          amount_std_dev
        }
        support_examples {
          transaction_id
          date
          description
          amount
          matched_doc_id
          matched_doc_description
          provider_name
          provider_rut
        }
        obligation_suggestion {
          obligation_type
          obligation_type_label
          obligation_family
          obligation_family_label
          suggested_description
          suggested_provider_rut
          suggested_provider_name
          suggested_amount
          account_plan
          is_received
        }
        suggested_doc_id
        suggested_doc_info
        confidence_score
        recommendation_reason
        created_at
        applied_at
        applied_by
      }
      total
      offset
      limit
    }
  }
`;

export const GetMatchRecommendations = (filter, pagination) => {
  const [result, reexecuteQuery] = useQuery({
    query: GET_MATCH_RECOMMENDATIONS,
    variables: { filter, pagination },
    pause: !filter?.company_id,
  });
  const { data, fetching, error } = result;
  return { data, fetching, error, reexecuteQuery };
};

// Query to get pending recommendations for a specific transaction
const GET_MATCH_RECOMMENDATIONS_BY_TRANSACTION = `
  query GetMatchRecommendationsByTransaction($filter: MatchRecommenderFilterInput!, $pagination: MatchRecommenderPaginationInput) {
    matchRecommendations(filter: $filter, pagination: $pagination) {
      items {
        id
        company_id
        pattern_hash
        type
        status
        target_transaction_id
        target_transaction
        detected_pattern {
          description_keywords
          transaction_type
          amount_min
          amount_max
          amount_exact
          provider_rut
          provider_name
          account_number
          obligation_type
          obligation_family
        }
        stats {
          occurrence_count
          first_seen
          last_seen
          consistency_score
          avg_amount
          amount_std_dev
        }
        obligation_suggestion {
          obligation_type
          obligation_type_label
          obligation_family
          obligation_family_label
          suggested_description
          suggested_provider_rut
          suggested_provider_name
          suggested_amount
          account_plan
          is_received
        }
        confidence_score
        recommendation_reason
        created_at
        applied_obligation_id
      }
      total
    }
  }
`;

export const GetMatchRecommendationsByTransaction = (companyId, transactionId) => {
  const filter = {
    company_id: companyId,
    target_transaction_id: transactionId,
    status: 'pending',
  };
  const pagination = { offset: 0, limit: 50 };
  
  const [result, reexecuteQuery] = useQuery({
    query: GET_MATCH_RECOMMENDATIONS_BY_TRANSACTION,
    variables: { filter, pagination },
    pause: !companyId || !transactionId,
    requestPolicy: 'network-only', // Siempre pedir datos frescos al abrir transacción (evitar caché vacía)
  });
  const { data, fetching, error } = result;
  return { data, fetching, error, reexecuteQuery };
};

const GET_MATCH_RECOMMENDER_METRICS = `
  query GetMatchRecommenderMetrics($companyId: String!) {
    matchRecommenderMetrics(companyId: $companyId) {
      total_pending
      total_approved
      total_rejected
      total_applied
      patterns_detected
      avg_confidence_score
      approval_rate
    }
  }
`;

export const GetMatchRecommenderMetrics = (companyId) => {
  const [result, reexecuteQuery] = useQuery({
    query: GET_MATCH_RECOMMENDER_METRICS,
    variables: { companyId },
    pause: !companyId,
  });
  const { data, fetching, error } = result;
  return { data, fetching, error, reexecuteQuery };
};

const GET_MATCH_RECOMMENDATION_DETAIL = `
  query GetMatchRecommendationDetail($recommendationId: String!, $companyId: String!) {
    matchRecommendationDetail(recommendationId: $recommendationId, companyId: $companyId) {
      id
      company_id
      pattern_hash
      type
      status
      target_transaction_id
      target_transaction
      detected_pattern {
        description_keywords
        transaction_type
        amount_min
        amount_max
        amount_exact
        provider_rut
        provider_name
        account_number
        obligation_type
        obligation_family
      }
      stats {
        occurrence_count
        first_seen
        last_seen
        consistency_score
        avg_amount
        amount_std_dev
      }
      support_examples {
        transaction_id
        date
        description
        amount
        matched_doc_id
        matched_doc_description
        provider_name
        provider_rut
      }
      obligation_suggestion {
        obligation_type
        obligation_type_label
        obligation_family
        obligation_family_label
        suggested_description
        suggested_provider_rut
        suggested_provider_name
        suggested_amount
        account_plan
        is_received
      }
      suggested_doc_id
      suggested_doc_info
      confidence_score
      recommendation_reason
      created_at
      applied_at
      applied_by
    }
  }
`;

export const GetMatchRecommendationDetail = (recommendationId) => {
  const [result, reexecuteQuery] = useQuery({
    query: GET_MATCH_RECOMMENDATION_DETAIL,
    variables: { recommendationId: recommendationId || '' },
    pause: !recommendationId,
    requestPolicy: 'network-only',
  });
  const { data, fetching, error } = result;
  return { data, fetching, error, reexecuteQuery };
};

// =============================================
// MUTATIONS
// =============================================

const GENERATE_MATCH_RECOMMENDATIONS = `
  mutation GenerateMatchRecommendations($input: GenerateRecommendationsInput!) {
    generateMatchRecommendations(input: $input) {
      success
      message
      recommendations_generated
      patterns_analyzed
      transactions_pending
    }
  }
`;

export const GenerateMatchRecommendations = () => {
  const [result, executeMutation] = useMutation(GENERATE_MATCH_RECOMMENDATIONS);
  return { ...result, executeMutation };
};

const APPROVE_MATCH_RECOMMENDATION = `
  mutation ApproveMatchRecommendation($recommendationId: String!, $companyId: String!) {
    approveMatchRecommendation(recommendationId: $recommendationId, companyId: $companyId) {
      success
      message
      created_obligation_id
      match_transaction_id
    }
  }
`;

export const ApproveMatchRecommendation = () => {
  const [result, executeMutation] = useMutation(APPROVE_MATCH_RECOMMENDATION);
  return { ...result, executeMutation };
};

const REJECT_MATCH_RECOMMENDATION = `
  mutation RejectMatchRecommendation($input: RejectRecommendationInput!, $companyId: String!) {
    rejectMatchRecommendation(input: $input, companyId: $companyId) {
      id
      status
    }
  }
`;

export const RejectMatchRecommendation = () => {
  const [result, executeMutation] = useMutation(REJECT_MATCH_RECOMMENDATION);
  return { ...result, executeMutation };
};

const UPDATE_MATCH_RECOMMENDATION_SUGGESTION = `
  mutation UpdateMatchRecommendationSuggestion($input: UpdateRecommendationSuggestionInput!, $companyId: String!) {
    updateMatchRecommendationSuggestion(input: $input, companyId: $companyId) {
      id
      obligation_suggestion {
        obligation_type
        obligation_type_label
        obligation_family
        obligation_family_label
        suggested_description
        suggested_provider_rut
        suggested_provider_name
        suggested_amount
        is_received
      }
    }
  }
`;

export const UpdateMatchRecommendationSuggestion = () => {
  const [result, executeMutation] = useMutation(UPDATE_MATCH_RECOMMENDATION_SUGGESTION);
  return { ...result, executeMutation };
};

const APPLY_MATCH_RECOMMENDATION = `
  mutation ApplyMatchRecommendation($input: ApplyRecommendationInput!, $companyId: String!) {
    applyMatchRecommendation(input: $input, companyId: $companyId) {
      success
      message
      created_obligation_id
      match_transaction_id
    }
  }
`;

export const ApplyMatchRecommendation = () => {
  const [result, executeMutation] = useMutation(APPLY_MATCH_RECOMMENDATION);
  return { ...result, executeMutation };
};

const MARK_RECOMMENDATION_APPLIED_BY_OBLIGATION = `
  mutation MarkRecommendationAppliedByObligation($obligationId: String!, $companyId: String!) {
    markRecommendationAppliedByObligation(obligationId: $obligationId, companyId: $companyId)
  }
`;

export const MarkRecommendationAppliedByObligation = () => {
  const [result, executeMutation] = useMutation(MARK_RECOMMENDATION_APPLIED_BY_OBLIGATION);
  return { ...result, executeMutation };
};
